import { getWebPath, TableDetailsTabType } from '@doss/common-all'
import { orderBy } from 'lodash'
import { useEffect, useMemo, useState } from 'react'
import { FiCode } from 'react-icons/fi'
import { useLocation, useNavigate } from 'react-router'
import { cn } from '~/@/lib/utils'
import {
  BreadcrumbObjectType,
  useGetWorkspaceByObjectLazyQuery,
} from '~/common/generated/apollo-operations'
import { useDeviceType } from '~/common/hooks/useDeviceQuery'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { Breadcrumb } from '~/common/utils/breadcrumbs'
import { retrieveLastViewedTab } from '~/common/utils/local-storage'
import { DossIcon } from '../DossIcon'
import { WorkspaceIcon } from '../navigation/WorkspaceIcon'
import { DetailedWorkspaceView } from '../renderers/workspaces/types'
import { RecordLink } from './RecordLink'
import { RotatingExpanderButton } from './RotatingExpanderButton'

type ViewLinkProps = {
  // embed is structural (id/name only) so both the detailed-workspace and the leaner
  // workspaceByObject child selections satisfy it.
  view: Pick<DetailedWorkspaceView, 'id' | 'table' | 'chart' | 'workspace'> & {
    embed?: { id: string; name: string } | null
  }
  breadcrumbs?: Breadcrumb[]
  expanded?: boolean
  indents?: number
}

// The view kinds ViewLink can render. Access is enforced server-side.
const isRenderableView = (view: ViewLinkProps['view']) =>
  !!(view.table?.id || view.chart?.id || view.workspace?.id || view.embed?.id)

export function ViewLink({ view, breadcrumbs, expanded = false, indents = 0 }: ViewLinkProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const asPath = location.pathname + location.search + location.hash
  const { subscribe, unsubscribe } = useDossSignals()
  const { user } = useGlobalContext()
  const { isMobile } = useDeviceType()
  const { viewObjectType, viewObjectName } = useMemo<{
    viewObjectType: BreadcrumbObjectType | undefined
    viewObjectName: string | null | undefined
  }>(() => {
    if (view.chart) {
      return {
        viewObjectType: BreadcrumbObjectType.CHART,
        viewObjectName: view.chart.name,
      }
    } else if (view.workspace) {
      return {
        viewObjectType: BreadcrumbObjectType.WORKSPACE,
        viewObjectName: view.workspace.name,
      }
    } else if (view.embed) {
      // Embeds have no BreadcrumbObjectType and no root workspace to expand into; the
      // undefined type keeps the workspaceByObject fetch effect from firing for them.
      return {
        viewObjectType: undefined,
        viewObjectName: view.embed.name,
      }
    } else {
      // Return table as default because the others are not used
      return {
        viewObjectType: BreadcrumbObjectType.TABLE,
        viewObjectName: view.table?.name,
      }
    }
  }, [view])

  const { dispatch } = useDossSignals()

  const [getRootWorkspaceData, rootWorkspaceData] = useGetWorkspaceByObjectLazyQuery({
    variables: {
      input: {
        objectId: view.table?.id || view.chart?.id || '',
        objectType: viewObjectType ?? BreadcrumbObjectType.TABLE,
      },
    },
    fetchPolicy: 'cache-and-network',
  })

  const views = useMemo(
    () => rootWorkspaceData.data?.workspaceByObject.views.filter(isRenderableView) || [],
    [rootWorkspaceData],
  )

  const expandable = useMemo(() => views.length > 0, [views])

  const active =
    asPath.includes(`/table/${view.table?.id}`) ||
    asPath.includes(`/chart/${view.chart?.id}`) ||
    asPath.includes(`/workspace/${view.workspace?.id}`) ||
    (!!view.embed && asPath.includes(`/embed/${view.embed.id}`))

  const navigateToView = () => {
    if (view.chart) {
      navigate(
        getWebPath({
          objectId: view.chart.id,
          objectType: BreadcrumbObjectType.CHART,
        }),
      )
      return
    } else if (view.workspace) {
      navigate(
        getWebPath({
          objectId: view.workspace.id,
          objectType: BreadcrumbObjectType.WORKSPACE,
        }),
      )
      return
    } else if (view.embed) {
      // No getWebPath/BreadcrumbObjectType entry for embeds; the literal path mirrors the
      // workspace dashboard's linkUrl and the standalone embed route.
      navigate(`/embed/${view.embed.id}`)
      return
    }
    let lastViewedTabId: string | null = null
    // Get the tab on the table that the user last viewed
    if (user && view.table) {
      lastViewedTabId = retrieveLastViewedTab(user.id, view.table.id)
    }
    // If the user has not viewed the table yet, route to the first table tab
    if (!lastViewedTabId) {
      lastViewedTabId = view.table ? view.table.tableTabs[0]?.id : null
    }

    navigate(
      getWebPath({
        objectId: view.table?.id,
        objectType: BreadcrumbObjectType.TABLE,
        tableParams: {
          tab: TableDetailsTabType.TABLE_TABS,
          id: lastViewedTabId || undefined,
        },
      }),
    )
  }

  const [isExpanded, setIsExpanded] = useState(expanded)

  // If the last breadcrumb is a child row of this view
  // then render the record link underneath this view
  const childRow = useMemo(() => {
    const lastBreadcrumb = breadcrumbs?.at(-1)
    const secondToLastBreadcrumb = breadcrumbs?.at(-2)
    const lastBreadcrumbIsChildRowOfView =
      lastBreadcrumb?.workspaceType === BreadcrumbObjectType.TABLE_ROW &&
      secondToLastBreadcrumb?.workspaceType === BreadcrumbObjectType.TABLE &&
      secondToLastBreadcrumb.link.includes(`/table/${view.table?.id}`)

    if (lastBreadcrumbIsChildRowOfView) {
      return lastBreadcrumb
    }
    return null
  }, [breadcrumbs, view])

  const orderedViews = useMemo(() => {
    return orderBy(views, (view) => view.order, ['asc'])
  }, [views])

  // Expand the view if it is present in the breadcrumbs
  useEffect(() => {
    const viewIsPresentInBreadcrumbs = !!breadcrumbs?.some(
      (breadcrumb) =>
        (breadcrumb.workspaceType === BreadcrumbObjectType.TABLE &&
          breadcrumb.name === view.table?.name) ||
        (breadcrumb.workspaceType === BreadcrumbObjectType.CHART &&
          breadcrumb.name === view.chart?.name),
    )
    if (viewIsPresentInBreadcrumbs) {
      setIsExpanded(true)
    }
  }, [breadcrumbs, view])

  useEffect(() => {
    if (
      viewObjectType === BreadcrumbObjectType.TABLE ||
      viewObjectType === BreadcrumbObjectType.CHART
    ) {
      getRootWorkspaceData()
    }
  }, [viewObjectType, getRootWorkspaceData])

  useEffect(() => {
    if (active) {
      setIsExpanded(active)
    }
  }, [active])

  useEffect(() => {
    const subscription = subscribe(DossSignalType.NEW_CHART_CREATED, async (id) => {
      if (id === rootWorkspaceData.data?.workspaceByObject.id) {
        getRootWorkspaceData()
      }
    })

    return () => {
      unsubscribe(subscription)
    }
  }, [rootWorkspaceData.data?.workspaceByObject.id, getRootWorkspaceData, subscribe, unsubscribe])

  const icon = view.table?.icon
  const iconClassName = cn(
    expandable && 'max-sm:hidden sm:group-hover/link:hidden',
    active && 'text-blue-8',
  )
  const iconSize = isMobile ? 16 : 18

  let iconDisplay: JSX.Element | undefined
  if (view.embed) {
    iconDisplay = <FiCode className={iconClassName} size={iconSize} />
  } else if (icon) {
    iconDisplay = <DossIcon className={iconClassName} name={icon.name} size={iconSize} />
  }

  return (
    <>
      <div key={view.id} className="relative">
        <div
          className={cn(
            'group/link flex py-2 px-3 w-full rounded overflow-hidden cursor-pointer items-center transition duration-200 z-10',
            active ? 'bg-gray-3' : 'hover:bg-gray-2',
            isMobile && 'active:bg-gray-4 active:scale-[0.98]',
          )}
          onClick={() => {
            if (isMobile) {
              // Delay menu dismiss so the user sees their selection before the menu closes
              setTimeout(() => {
                dispatch(DossSignalType.TOGGLE_MOBILE_MENU, false)
              }, 300)
            } else {
              dispatch(DossSignalType.TOGGLE_MOBILE_MENU, false)
            }
            navigateToView()
          }}
        >
          {Array.from({ length: indents }).map((_, i) => (
            <div key={i} className="h-4 w-5" />
          ))}
          <div className="flex w-full flex-1 items-center gap-2 overflow-hidden">
            <div className={cn('flex items-center', isMobile ? 'size-4' : 'size-5')}>
              <WorkspaceIcon
                className={cn(
                  expandable && 'max-sm:hidden sm:group-hover/link:hidden',
                  active && 'text-blue-8',
                )}
                icon={iconDisplay}
                size={isMobile ? 16 : 18}
                workspaceType={viewObjectType}
              />
              {expandable && (
                <RotatingExpanderButton
                  expandable={expandable}
                  expanded={isExpanded}
                  onClick={(e) => {
                    e.stopPropagation()
                    setIsExpanded((v) => !v)
                  }}
                />
              )}
            </div>
            <div className={cn('flex-1 overflow-hidden', active ? 'text-gray-12' : 'text-gray-10')}>
              <div className="w-full truncate text-sm">{viewObjectName}</div>
            </div>
          </div>
        </div>
      </div>
      {childRow && <RecordLink indents={indents + 1} record={childRow} />}
      <div className={cn(isExpanded ? '' : 'hidden')}>
        {orderedViews.map((view) => (
          <ViewLink key={view.id} breadcrumbs={breadcrumbs} indents={indents + 1} view={view} />
        ))}
      </div>
    </>
  )
}
